export type ActivityLevel = "low" | "medium" | "high";

/** Activity thresholds from docs/08-web-ui.md: 0–20 low, 21–50 medium, 51–100 high. */
export function activityLevel(percent: number): ActivityLevel {
  if (percent <= 20) return "low";
  if (percent <= 50) return "medium";
  return "high";
}

const durationUnits = {
  ru: { h: "ч", m: "мин" },
  en: { h: "h", m: "m" },
} as const;

/** Initials from a full name: "Анна Смирнова" → "АС". */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

/**
 * Formats a duration as hours and minutes: "7 ч 42 мин" (ru), "7h 42m" (en).
 * Seconds are truncated; zero is "0 мин" / "0m".
 */
export function formatDuration(totalSeconds: number, locale: string): string {
  const lang = locale.split("-")[0];
  const isRu = lang === "ru";
  const u = isRu ? durationUnits.ru : durationUnits.en;
  const sep = isRu ? " " : "";

  const minutesTotal = Math.max(0, Math.floor(totalSeconds / 60));
  const hours = Math.floor(minutesTotal / 60);
  const minutes = minutesTotal % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}${sep}${u.h}`);
  if (minutes > 0 || hours === 0) parts.push(`${minutes}${sep}${u.m}`);
  return parts.join(" ");
}

/** Formats a date-time in the given locale and time zone. */
export function formatDateTime(value: string | Date, locale: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "medium",
    timeZone,
  }).format(typeof value === "string" ? new Date(value) : value);
}

/** "10:40" in the given time zone. */
export function formatTime(value: string | Date, locale: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).format(typeof value === "string" ? new Date(value) : value);
}

/** Calendar date (YYYY-MM-DD) of an instant in the given time zone. */
export function dateInZone(value: string | Date, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).formatToParts(typeof value === "string" ? new Date(value) : value);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Hour (0–23) of an instant in the given time zone. */
export function hourInZone(value: string | Date, timeZone?: string): number {
  const h = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", hourCycle: "h23", timeZone }).format(
    typeof value === "string" ? new Date(value) : value,
  );
  return Number(h);
}

/** Minutes since midnight of an instant in the given time zone. */
export function minutesInZone(value: string | Date, timeZone?: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).formatToParts(typeof value === "string" ? new Date(value) : value);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return get("hour") * 60 + get("minute") + get("second") / 60;
}

/** Adds days to a YYYY-MM-DD date. */
export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** "среда, 16 сентября 2026 г." for a YYYY-MM-DD date. */
export function formatLongDate(date: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00Z`));
}

/** Compact date and time: "16 сент., 18:23"; the year is added when it is not the current one. */
export function formatShortDateTime(value: string | Date, locale: string, timeZone?: string): string {
  const d = typeof value === "string" ? new Date(value) : value;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: sameYear ? undefined : "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).format(d);
}

/** Short weekday and day of month for a YYYY-MM-DD date: "Пн 14" / "Mon 14". */
export function formatWeekday(date: string, locale: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(d);
  const name = weekday.charAt(0).toUpperCase() + weekday.slice(1).replace(/\.$/, "");
  return `${name} ${d.getUTCDate()}`;
}

/** "14–20 сентября" for a week given by its first day. */
export function formatWeekRange(start: string, locale: string): string {
  const from = new Date(`${start}T12:00:00Z`);
  const to = new Date(`${shiftDate(start, 6)}T12:00:00Z`);
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", timeZone: "UTC" }).formatRange(
    from,
    to,
  );
}

/** Share in whole percent; 0 when the total is empty. */
export function percentOf(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

/** Time for today's instants, date and time for older ones. */
export function formatSince(value: string, locale: string, timeZone?: string): string {
  return dateInZone(value, timeZone) === dateInZone(new Date(), timeZone)
    ? formatTime(value, locale, timeZone)
    : formatShortDateTime(value, locale, timeZone);
}

/** Monday of the week that contains a YYYY-MM-DD date. */
export function weekStartOf(date: string): string {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return shiftDate(date, -((day + 6) % 7));
}

/** "23 сент." / "Sep 23" for a YYYY-MM-DD date; the year is added when it is not the current one. */
export function formatDayMonth(date: string, locale: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  const sameYear = d.getUTCFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: sameYear ? undefined : "numeric",
    timeZone: "UTC",
  }).format(d);
}

/** Offset of a time zone from UTC at an instant, in milliseconds. */
function zoneOffset(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const local = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return local - Math.floor(instant / 1000) * 1000;
}

/** The instant of a wall-clock date ("2026-09-16") and time ("10:30") in a time zone, as ISO. */
export function zonedToISO(date: string, time: string, timeZone: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const [h, min] = time.split(":").map(Number);
  const wall = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, h ?? 0, min ?? 0);
  let instant = wall - zoneOffset(wall, timeZone);
  // A second pass fixes the offset when a DST change lies between the guesses.
  instant = wall - zoneOffset(instant, timeZone);
  return new Date(instant).toISOString();
}

/** "ср, 16 сент." for a YYYY-MM-DD date; the year is added when it is not the current one. */
export function formatShortDate(date: string, locale: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  const sameYear = d.getUTCFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: sameYear ? undefined : "numeric",
    timeZone: "UTC",
  }).format(d);
}

/** "1–15 сентября 2026 г." for a period of YYYY-MM-DD dates. */
export function formatPeriod(from: string, to: string, locale: string): string {
  const fmt = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const a = new Date(`${from}T12:00:00Z`);
  return from === to ? fmt.format(a) : fmt.formatRange(a, new Date(`${to}T12:00:00Z`));
}
